// Single source of truth for the analytics date-range filter + quick presets.
// Replaces the formatDateISO / applyQuickRange / toggle-sync `watch` blocks that were
// copy-pasted verbatim across AnalyticsDashboard, AnalyticsSupplierView and
// SupplierGroupAnalytics.
//
// Usage:
//   const { startDate, endDate, activeToggle, quickToggles, applyQuickRange, maxDate }
//     = useDateRange(loadData)
// `onChange` runs on any valid range change (not on initial mount — the view triggers
// its own first fetch in onMounted, matching the previous behaviour).

import { ref, watch } from 'vue'

function formatISO(d) {
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function startOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

// Preset definitions. Each `range()` returns [startDate, endDate] as Date objects.
// Computed lazily off "now" so they stay correct across day boundaries.
function buildPresets() {
  const presets = [
    {
      label: 'This Month',
      range: () => {
        const now = new Date()
        return [new Date(now.getFullYear(), now.getMonth(), 1), now]
      },
    },
    {
      label: 'Last Month',
      range: () => {
        const now = new Date()
        return [
          new Date(now.getFullYear(), now.getMonth() - 1, 1),
          new Date(now.getFullYear(), now.getMonth(), 0),
        ]
      },
    },
    {
      label: 'Last 7 Days',
      range: () => {
        const now = startOfDay(new Date())
        const s = new Date(now)
        s.setDate(s.getDate() - 6)
        return [s, new Date()]
      },
    },
    {
      label: 'Last 30 Days',
      range: () => {
        const now = startOfDay(new Date())
        const s = new Date(now)
        s.setDate(s.getDate() - 29)
        return [s, new Date()]
      },
    },
    {
      label: 'Last 90 Days',
      range: () => {
        const now = startOfDay(new Date())
        const s = new Date(now)
        s.setDate(s.getDate() - 89)
        return [s, new Date()]
      },
    },
    {
      label: 'This Year',
      range: () => {
        const now = new Date()
        return [new Date(now.getFullYear(), 0, 1), now]
      },
    },
  ]
  return presets
}

export function useDateRange(onChange) {
  const presets = buildPresets()
  const [initStart, initEnd] = presets[0].range()

  const startDate = ref(formatISO(initStart))
  const endDate = ref(formatISO(initEnd))
  const activeToggle = ref(presets[0].label)
  const maxDate = formatISO(new Date())

  const applyQuickRange = (preset) => {
    const [s, e] = preset.range()
    startDate.value = formatISO(s)
    endDate.value = formatISO(e)
    // activeToggle is set by the watcher below (keeps a single code path).
  }

  const syncActiveToggle = () => {
    const match = presets.find((p) => {
      const [s, e] = p.range()
      return formatISO(s) === startDate.value && formatISO(e) === endDate.value
    })
    activeToggle.value = match ? match.label : null
  }

  watch([startDate, endDate], () => {
    syncActiveToggle()
    // Guard against an inverted range (end before start) — don't fetch garbage.
    if (endDate.value < startDate.value) return
    if (typeof onChange === 'function') onChange()
  })

  return {
    startDate,
    endDate,
    activeToggle,
    quickToggles: presets,
    applyQuickRange,
    maxDate,
  }
}
